import {
  STEP_LABELS,
  TRIGGER_LABELS,
  workflowStepSchema,
  type WorkflowStep,
  type WorkflowTrigger,
} from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApi } from '../api-context.tsx'
import { notify, notifyFailure } from '../feedback.ts'
import { messagingKeys } from './keys.ts'
import { templateLabel } from './useMessages.ts'

/** Workflows: the list, the step-list builder, and the people in each. */

export function useWorkflows() {
  const api = useApi()
  return useQuery({
    queryKey: messagingKeys.workflows(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.workflows.get()
      if (error) throw error
      return data
    },
  })
}

export type WorkflowSummary = NonNullable<ReturnType<typeof useWorkflows>['data']>[number]

export function useTeam() {
  const api = useApi()
  return useQuery({
    queryKey: messagingKeys.team(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.workflows.team.get()
      if (error) throw error
      return data
    },
    staleTime: 5 * 60_000,
  })
}

export function useCreateWorkflow() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { name: string; trigger: WorkflowTrigger }) => {
      const { data, error } = await api.api.v1.workflows.post({
        ...input,
        triggerConfig: {},
        steps: [],
      })
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: messagingKeys.workflows() }),
  })
}

/** A step as the builder edits it: a type, and fields that may still be blank. */
export type Step = { type: string } & Record<string, unknown>

interface Edits {
  name: string
  trigger: WorkflowTrigger
  triggerConfig: { tagId?: string; weeks?: number }
  steps: Step[]
}

/** A step with blanks the builder fills in. */
export function blankStep(type: WorkflowStep['type'], defaults: { userId?: string } = {}): Step {
  switch (type) {
    case 'send_email':
      return { type, subject: '', body: 'Hi {{firstName}},\n\n' }
    case 'send_sms':
      return { type, body: '' }
    case 'wait':
      return { type, days: 7, hours: 0 }
    case 'assign_task':
      return { type, title: '', assigneeUserId: defaults.userId ?? '', dueInDays: 3 }
    case 'add_tag':
      return { type, tagId: '' }
    case 'add_to_group':
      return { type, groupId: '' }
    case 'branch':
      return { type, condition: { hasTagId: '', negate: false } }
  }
}

export const STEP_TYPES = Object.keys(STEP_LABELS) as WorkflowStep['type'][]
export { STEP_LABELS, TRIGGER_LABELS }

/** One line a person can read: "Wait 7 days", "Send an email: Welcome". */
export function describeStep(step: Step): string {
  const type = step.type as WorkflowStep['type']
  if (type === 'wait') {
    const days = Number(step.days ?? 0)
    const hours = Number(step.hours ?? 0)
    return `Wait ${days ? `${days} day${days === 1 ? '' : 's'}` : ''}${days && hours ? ' and ' : ''}${hours ? `${hours} hour${hours === 1 ? '' : 's'}` : ''}${!days && !hours ? 'no time' : ''}`
  }
  if (type === 'send_email')
    return `Send an email: ${templateLabel(String(step.subject ?? '')) || '(no subject)'}`
  if (type === 'send_sms')
    return `Send a text: ${templateLabel(String(step.body ?? ''), 60) || '(empty)'}`
  if (type === 'assign_task') return `Assign a task: ${String(step.title || '(untitled)')}`
  return STEP_LABELS[type] ?? String(type)
}

/**
 * The builder: a workflow's name, trigger and steps as an editable copy, each
 * step validated by the same schema the server uses, saved as a whole.
 */
export function useWorkflowBuilder(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const workflow = useQuery({
    queryKey: messagingKeys.workflow(id),
    queryFn: async () => {
      const { data, error } = await api.api.v1.workflows({ id }).get()
      if (error) throw error
      return data
    },
  })
  const [edits, setEdits] = useState<Edits | null>(null)
  const loaded = workflow.data
  const current: Edits | null =
    edits ??
    (loaded
      ? {
          name: loaded.name,
          trigger: loaded.trigger as WorkflowTrigger,
          triggerConfig: loaded.triggerConfig ?? {},
          steps: loaded.steps,
        }
      : null)
  const edit = (change: (draft: Edits) => Edits) => {
    if (current) setEdits(change(current))
  }

  const stepErrors = (current?.steps ?? []).map((step) => {
    const parsed = workflowStepSchema.safeParse(step)
    return parsed.success ? null : (parsed.error.issues[0]?.message ?? 'Incomplete')
  })

  const put = (data: unknown) => {
    queryClient.setQueryData(messagingKeys.workflow(id), data)
    void queryClient.invalidateQueries({ queryKey: messagingKeys.workflows() })
  }
  const save = useMutation({
    mutationFn: async () => {
      if (!current) throw new Error('Nothing to save')
      const { data, error } = await api.api.v1.workflows({ id }).put(current)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      setEdits(null)
      put(data)
    },
  })
  const setActive = useMutation({
    mutationFn: async (active: boolean) => {
      const { data, error } = await api.api.v1.workflows({ id }).active.post({ active })
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const start = useMutation({
    mutationFn: async (personIds: string[]) => {
      const { data, error } = await api.api.v1.workflows({ id }).start.post({ personIds })
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: messagingKeys.workflow(id) }),
  })
  const resumeFailed = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.workflows({ id })['resume-failed'].post()
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: messagingKeys.workflow(id) }),
  })
  const cancelInstance = useMutation({
    mutationFn: async (instanceId: string) => {
      const { error } = await api.api.v1.workflows.instances({ id: instanceId }).cancel.post()
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: messagingKeys.workflow(id) }),
  })

  return {
    workflow: loaded ?? null,
    isLoading: workflow.isPending,
    error: workflow.error,
    current,
    dirty: edits !== null,
    setName: (name: string) => edit((d) => ({ ...d, name })),
    setTrigger: (trigger: WorkflowTrigger) =>
      edit((d) => ({
        ...d,
        trigger,
        triggerConfig: trigger === 'missed_attendance' ? { weeks: 3 } : {},
      })),
    setTriggerConfig: (triggerConfig: Edits['triggerConfig']) =>
      edit((d) => ({ ...d, triggerConfig })),
    addStep: (step: Step) => edit((d) => ({ ...d, steps: [...d.steps, step] })),
    updateStep: (index: number, change: Record<string, unknown>) =>
      edit((d) => ({
        ...d,
        steps: d.steps.map((s, i) => (i === index ? { ...s, ...change } : s)),
      })),
    removeStep: (index: number) =>
      edit((d) => ({ ...d, steps: d.steps.filter((_, i) => i !== index) })),
    moveStep: (index: number, by: -1 | 1) =>
      edit((d) => {
        const steps = [...d.steps]
        const target = index + by
        if (target < 0 || target >= steps.length) return d
        const [moved] = steps.splice(index, 1)
        if (moved) steps.splice(target, 0, moved)
        return { ...d, steps }
      }),
    discard: () => setEdits(null),
    stepErrors,
    canSave: Boolean(current?.name.trim()) && stepErrors.every((e) => e === null),
    save,
    setActive,
    start,
    cancelInstance,
    resumeFailed,
    /** Journeys that stopped on a failed send, and can be resumed. */
    // The workflow's own total — the instance list on the page is only the latest.
    stopped: loaded?.failed ?? 0,
  }
}

// ---- My Tasks -----------------------------------------------------------------------

/** My Tasks: open first (overdue, then due, then snoozed), done at the bottom. */
export function useMyTasks() {
  const api = useApi()
  const queryClient = useQueryClient()
  const tasks = useQuery({
    queryKey: messagingKeys.tasks(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.me.tasks.get()
      if (error) throw error
      return data
    },
  })
  const put = (data: unknown) => queryClient.setQueryData(messagingKeys.tasks(), data)
  const create = useMutation({
    mutationFn: async (body: {
      title: string
      dueOn?: string | null
      personId?: string | null
      notes?: string | null
    }) => {
      const { data, error } = await api.api.v1.me.tasks.post(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const update = useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      status?: 'open' | 'done'
      snoozedUntil?: string | null
      dueOn?: string | null
      assigneeUserId?: string
      notes?: string | null
    }) => {
      const { data, error } = await api.api.v1.me.tasks({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const all = tasks.data ?? []
  const open = all.filter((t) => t.status === 'open')
  type Patch = Parameters<typeof update.mutate>[0]
  /**
   * A change that shows at once (the cache moves first) and puts itself back,
   * saying so with Retry, if the server refuses it.
   */
  const change = (patch: Patch, about: string, local?: (task: (typeof all)[number]) => object) => {
    const before = queryClient.getQueryData(messagingKeys.tasks())
    if (local)
      queryClient.setQueryData(messagingKeys.tasks(), (old: typeof all | undefined) =>
        old?.map((t) => (t.id === patch.id ? { ...t, ...local(t) } : t)),
      )
    update.mutate(patch, {
      onError: (error) => {
        queryClient.setQueryData(messagingKeys.tasks(), before)
        notifyFailure(error, () => change(patch, about, local), about)
      },
    })
  }
  const titleOf = (id: string) => all.find((t) => t.id === id)?.title ?? 'the task'
  const reopen = (id: string) =>
    change({ id, status: 'open' }, 'Couldn’t reopen it', () => ({ status: 'open' }))
  return {
    /** The query itself, for a screen's loading / failed / empty states. */
    query: tasks,
    isLoading: tasks.isPending,
    error: tasks.error ?? create.error,
    overdue: open.filter((t) => t.overdue && !t.snoozed),
    upcoming: open.filter((t) => !t.overdue && !t.snoozed),
    snoozed: open.filter((t) => t.snoozed),
    done: all.filter((t) => t.status === 'done').slice(0, 20),
    create,
    /** Ticks it off now, with Undo. */
    complete: (id: string) => {
      const title = titleOf(id)
      change({ id, status: 'done' }, 'Couldn’t mark it done', () => ({ status: 'done' }))
      notify({
        title: `Done: ${title}`,
        description: 'Moved to Done.',
        action: { label: 'Undo', run: () => reopen(id) },
      })
    },
    reopen: (id: string) => {
      reopen(id)
      notify({ title: `Reopened: ${titleOf(id)}`, tone: 'info' })
    },
    snooze: (id: string, days: number) => {
      const snoozedUntil = new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10)
      change({ id, snoozedUntil }, 'Couldn’t snooze it')
      notify({
        title: `Snoozed: ${titleOf(id)}`,
        description: days === 1 ? 'It comes back tomorrow.' : `It comes back in ${days} days.`,
        action: {
          label: 'Undo',
          run: () => change({ id, snoozedUntil: null }, 'Couldn’t wake it'),
        },
      })
    },
    /** A chosen day, or `null` to wake it now. */
    snoozeUntil: (id: string, snoozedUntil: string | null) =>
      change({ id, snoozedUntil }, snoozedUntil ? 'Couldn’t snooze it' : 'Couldn’t wake it'),
    /** When it should be done: a day, or `null` for no due date. Not a snooze. */
    setDue: (id: string, dueOn: string | null) =>
      change({ id, dueOn }, 'Couldn’t change the due date'),
    reassign: (id: string, assigneeUserId: string, name?: string) => {
      change({ id, assigneeUserId }, 'Couldn’t hand it on')
      notify({
        title: `Handed on: ${titleOf(id)}`,
        description: name ? `It’s on ${name}’s list now.` : undefined,
      })
    },
    annotate: (id: string, notes: string | null) =>
      change({ id, notes }, 'Couldn’t save the notes'),
  }
}

export type MyTask = ReturnType<typeof useMyTasks>['overdue'][number]
